import type {
  CastingInput,
  CastingRow,
  ClientDetail,
  ClientInput,
  ClientWithStats,
  CurrentUser,
  DashboardSummary,
  DeliverableDetail,
  DeliverableInput,
  DeliverableRow,
  Document,
  IntakeBatch,
  IntakeBatchDetail,
  IntakePreview,
  IntakeRow,
  Language,
  LanguageVersionInput,
  LanguageVersionRow,
  Market,
  OutstandingItem,
  Paginated,
  Project,
  ProjectDetail,
  ProjectInput,
  ProjectMember,
  ProjectWithStats,
  Review,
  ReviewInput,
  Vendor,
} from '@bedouin/shared'
import { api, type QueryValue } from './client'

type Query = Record<string, QueryValue>

/**
 * One typed function per endpoint. Views and stores call these rather than
 * assembling URLs, so a route change lands in exactly one place.
 */
export const resources = {
  me: (signal?: AbortSignal) => api.get<CurrentUser>('/api/me', undefined, signal),

  dashboard: {
    summary: (query?: Query, signal?: AbortSignal) =>
      api.get<DashboardSummary>('/api/dashboard/summary', query, signal),
    outstanding: (query?: Query, signal?: AbortSignal) =>
      api.get<OutstandingItem[]>('/api/dashboard/outstanding', query, signal),
    calendar: (query?: Query, signal?: AbortSignal) =>
      api.get<OutstandingItem[]>('/api/dashboard/calendar', query, signal),
  },

  clients: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<ClientWithStats[]>('/api/clients', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<ClientDetail>(`/api/clients/${id}`, undefined, signal),
    create: (body: ClientInput) => api.post<ClientWithStats>('/api/clients', body),
    update: (id: string, body: Partial<ClientInput>) =>
      api.patch<ClientWithStats>(`/api/clients/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/clients/${id}`),
  },

  projects: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<ProjectWithStats[]>('/api/projects', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<ProjectDetail>(`/api/projects/${id}`, undefined, signal),
    create: (body: ProjectInput) => api.post<Project>('/api/projects', body),
    update: (id: string, body: Partial<ProjectInput>) =>
      api.patch<Project>(`/api/projects/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/projects/${id}`),
    members: (id: string) => api.get<ProjectMember[]>(`/api/projects/${id}/members`),
    addMember: (id: string, body: Record<string, unknown>) =>
      api.post<ProjectMember>(`/api/projects/${id}/members`, body),
    removeMember: (id: string, memberId: string) =>
      api.delete<{ id: string }>(`/api/projects/${id}/members/${memberId}`),
  },

  deliverables: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<DeliverableRow>>('/api/deliverables', query, signal),
    get: (id: string, signal?: AbortSignal) =>
      api.get<DeliverableDetail>(`/api/deliverables/${id}`, undefined, signal),
    create: (body: DeliverableInput & { language_codes?: string[] }) =>
      api.post<DeliverableRow>('/api/deliverables', body),
    update: (id: string, body: Partial<DeliverableInput>) =>
      api.patch<DeliverableRow>(`/api/deliverables/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/deliverables/${id}`),
    bulkStatus: (ids: string[], status: string) =>
      api.post<{ updated: number }>('/api/deliverables/bulk-status', { ids, status }),
  },

  languageVersions: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<LanguageVersionRow>>('/api/language-versions', query, signal),
    get: (id: string) => api.get<LanguageVersionRow>(`/api/language-versions/${id}`),
    create: (body: LanguageVersionInput) =>
      api.post<LanguageVersionRow>('/api/language-versions', body),
    update: (id: string, body: Partial<LanguageVersionInput>) =>
      api.patch<LanguageVersionRow>(`/api/language-versions/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/language-versions/${id}`),
    bulkAdd: (deliverableId: string, languageCodes: string[], extra?: Record<string, unknown>) =>
      api.post<LanguageVersionRow[]>('/api/language-versions/bulk', {
        deliverable_id: deliverableId,
        language_codes: languageCodes,
        ...extra,
      }),
  },

  casting: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Paginated<CastingRow>>('/api/casting', query, signal),
    get: (id: string) => api.get<CastingRow>(`/api/casting/${id}`),
    create: (body: CastingInput) => api.post<CastingRow>('/api/casting', body),
    update: (id: string, body: Partial<CastingInput>) =>
      api.patch<CastingRow>(`/api/casting/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/casting/${id}`),
  },

  vendors: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Vendor[]>('/api/vendors', query, signal),
    create: (body: Record<string, unknown>) => api.post<Vendor>('/api/vendors', body),
    update: (id: string, body: Record<string, unknown>) =>
      api.patch<Vendor>(`/api/vendors/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/vendors/${id}`),
  },

  markets: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Market[]>('/api/markets', query, signal),
    create: (body: Record<string, unknown>) => api.post<Market>('/api/markets', body),
    update: (id: string, body: Record<string, unknown>) =>
      api.patch<Market>(`/api/markets/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/markets/${id}`),
  },

  languages: {
    list: (query?: Query) => api.get<Language[]>('/api/languages', query),
  },

  documents: {
    list: (query?: Query, signal?: AbortSignal) =>
      api.get<Document[]>('/api/documents', query, signal),
    createLink: (body: Record<string, unknown>) => api.post<Document>('/api/documents', body),
    upload: (form: FormData) => api.upload<Document>('/api/documents/upload', form),
    downloadUrl: (id: string) => api.url(`/api/documents/${id}/download`),
    remove: (id: string) => api.delete<{ id: string }>(`/api/documents/${id}`),
  },

  reviews: {
    list: (query?: Query, signal?: AbortSignal) => api.get<Review[]>('/api/reviews', query, signal),
    create: (body: ReviewInput) => api.post<Review>('/api/reviews', body),
    update: (id: string, body: Partial<ReviewInput>) => api.patch<Review>(`/api/reviews/${id}`, body),
    remove: (id: string) => api.delete<{ id: string }>(`/api/reviews/${id}`),
  },

  intake: {
    preview: (headers: string[], rows?: Record<string, unknown>[]) =>
      api.post<IntakePreview>('/api/intake/preview', { headers, rows }),
    batches: (query?: Query, signal?: AbortSignal) =>
      api.get<IntakeBatch[]>('/api/intake/batches', query, signal),
    batch: (id: string, signal?: AbortSignal) =>
      api.get<IntakeBatchDetail>(`/api/intake/batches/${id}`, undefined, signal),
    createBatch: (body: Record<string, unknown>) =>
      api.post<IntakeBatch>('/api/intake/batches', body),
    remap: (id: string, columnMap: Record<string, string>) =>
      api.post<{ remapped: number }>(`/api/intake/batches/${id}/remap`, { column_map: columnMap }),
    updateRow: (id: string, body: Record<string, unknown>) =>
      api.patch<IntakeRow>(`/api/intake/rows/${id}`, body),
    import: (id: string, includePending = false) =>
      api.post<{
        imported: number
        deliverable_ids: string[]
        needs_attention: number
        unmatched_references: string[]
      }>(`/api/intake/batches/${id}/import`, { include_pending: includePending }),
    removeBatch: (id: string) => api.delete<{ id: string }>(`/api/intake/batches/${id}`),
  },
}
